import {
  AttachFile as AttachIcon,
  CheckCircleOutline as DoneIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorIcon,
  InsertDriveFileOutlined as FileIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  alpha,
} from "@mui/material";
import Button from "components/common/Button";
import { createFileEntry } from "fileUtils";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { useAttachIngestionFilesMutation } from "services/aiAgents";
import { filesApi } from "services/filesService";
import {
  FILE_URL_EXPIRY_SECONDS,
  formatFileSize,
  isSupportedIngestionFile,
} from "utils/ingestionFiles";

/**
 * Attach more documents to an existing triage item.
 *
 * Files are uploaded, then attached in one call — the backend re-ingests the
 * combined set and marks the existing report stale until the next triage run.
 */
const AttachFilesDialog = ({
  open,
  onClose,
  item,
  onAttached,
  initialFiles,
}) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachIngestionFiles] = useAttachIngestionFilesMutation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const onDrop = useCallback(
    (acceptedFiles) => {
      const supported = [];
      for (const file of acceptedFiles) {
        if (isSupportedIngestionFile(file.name)) {
          supported.push(file);
        } else {
          enqueueSnackbar(`Unsupported file type: ${file.name}`, {
            variant: "warning",
          });
        }
      }
      setPendingFiles((prev) => [
        ...prev,
        ...supported.map((file) => ({
          key: `${file.name}-${Date.now()}-${Math.random()}`,
          file,
          status: "ready", // ready | uploading | done | error
          error: null,
        })),
      ]);
    },
    [enqueueSnackbar]
  );

  // Files dropped onto the report drawer's Documents block arrive here rather than being
  // uploaded there, so one code path validates, queues and reports them.
  useEffect(() => {
    if (open && initialFiles?.length) onDrop(initialFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isAttaching,
  });

  const setFileStatus = (key, status, error = null, uploaded = undefined) =>
    setPendingFiles((prev) =>
      prev.map((f) =>
        f.key === key
          ? { ...f, status, error, ...(uploaded !== undefined && { uploaded }) }
          : f
      )
    );

  const removeFile = (key) =>
    setPendingFiles((prev) => prev.filter((f) => f.key !== key));

  const pendingCount = pendingFiles.filter((f) => f.status !== "done").length;

  const handleClose = () => {
    if (isAttaching) return;
    setPendingFiles([]);
    onClose();
  };

  const handleAttach = async () => {
    if (!item || pendingFiles.length === 0) return;
    setIsAttaching(true);
    try {
      const uploaded = [];
      const failed = [];
      for (const pending of pendingFiles) {
        // A retry after a failed attach must not upload the same file twice — that
        // would orphan the first copy and register a second file id for one document.
        if (pending.status === "done" && pending.uploaded) {
          uploaded.push(pending.uploaded);
          continue;
        }
        try {
          setFileStatus(pending.key, "uploading");
          const { id: fileId, hash, size } = await createFileEntry(
            pending.file,
            false,
            null,
            undefined,
            true
          );
          // Always forceRefetch so we never register a cached (expired) presigned URL
          const fileUrlResult = await dispatch(
            filesApi.endpoints.fileUrl.initiate(
              { fileId, expiry: FILE_URL_EXPIRY_SECONDS },
              { forceRefetch: true }
            )
          ).unwrap();
          const record = {
            file_id: fileId,
            file_name: pending.file.name,
            file_hash: hash,
            file_size: size,
            file_url: fileUrlResult.url,
          };
          uploaded.push(record);
          setFileStatus(pending.key, "done", null, record);
        } catch (error) {
          failed.push(pending.file.name);
          setFileStatus(pending.key, "error", error.message);
        }
      }

      if (uploaded.length === 0) {
        enqueueSnackbar("No files could be uploaded", { variant: "error" });
        return;
      }

      const attachResult = await attachIngestionFiles({
        fileId: item.folder_id ? null : item.file_id,
        folderId: item.folder_id || null,
        files: uploaded,
      }).unwrap();

      // The server skips a file whose content is already on the item, so counting what we
      // uploaded would tell the user a document is attached that deliberately was not.
      const skipped = attachResult?.already_attached ?? [];
      const added = uploaded.length - skipped.length;

      if (skipped.length > 0) {
        enqueueSnackbar(
          `Already attached, so not added again: ${skipped.join(", ")}`,
          { variant: "warning" }
        );
      }
      if (added > 0) {
        enqueueSnackbar(
          `${added} file${
            added !== 1 ? "s" : ""
          } attached — the documents are being processed; re-run triage to update the report`,
          { variant: "success" }
        );
      }
      onAttached && onAttached();
      if (failed.length > 0) {
        // Saying "attached" and closing would claim files that never uploaded. The
        // rows stay so the reader can see which ones, and retry just those.
        enqueueSnackbar(
          `${failed.length} file${
            failed.length !== 1 ? "s" : ""
          } could not be uploaded: ${failed.join(", ")}`,
          { variant: "error", persist: true }
        );
        return;
      }
      setPendingFiles([]);
      onClose();
    } catch (error) {
      // attachIngestionFiles already prefixes ("Failed to attach files: …"), so
      // prefixing again would show the reader the phrase twice.
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setIsAttaching(false);
    }
  };

  const statusIcon = (status) => {
    if (status === "uploading")
      return <CircularProgress size={16} thickness={5} />;
    if (status === "done")
      return <DoneIcon sx={{ fontSize: "1.1rem", color: "success.main" }} />;
    if (status === "error")
      return <ErrorIcon sx={{ fontSize: "1.1rem", color: "error.main" }} />;
    return <FileIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"sm"}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AttachIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {"Add files"}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
          {`New documents join “${
            item?.file_name ?? ""
          }” and the next triage run evaluates the combined set.`}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          data-testid={"attach-files-dropzone"}
          sx={(theme) => ({
            border: `1.5px dashed ${
              isDragActive ? theme.palette.primary.main : theme.palette.divider
            }`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: isAttaching ? "default" : "pointer",
            bgcolor: isDragActive
              ? alpha(theme.palette.primary.main, 0.04)
              : "transparent",
            transition: "border-color 120ms, background-color 120ms",
          })}
        >
          <input {...getInputProps()} data-testid={"attach-files-input"} />
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
            {isDragActive
              ? "Drop the files here"
              : "Drag & drop files here, or click to browse"}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.5 }}>
            {"PDF, Word, Excel, CSV, TXT, HTML or ZIP"}
          </Typography>
        </Box>

        {pendingFiles.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {pendingFiles.map(({ key, file, status, error }) => (
              <ListItem
                key={key}
                secondaryAction={
                  status !== "uploading" && !isAttaching ? (
                    <IconButton
                      edge={"end"}
                      size={"small"}
                      onClick={() => removeFile(key)}
                    >
                      <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  ) : null
                }
                sx={{ px: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {statusIcon(status)}
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={error ?? formatFileSize(file.size)}
                  primaryTypographyProps={{ sx: { fontSize: 13 } }}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: 11.5,
                      color: error ? "error.main" : "text.disabled",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          size={"small"}
          color={"secondary"}
          onClick={handleClose}
          disabled={isAttaching}
          sx={{ textTransform: "none" }}
        >
          {"Cancel"}
        </Button>
        <Button
          size={"small"}
          onClick={handleAttach}
          disabled={isAttaching || pendingFiles.length === 0}
          data-testid={"attach-files-submit"}
          startIcon={
            isAttaching ? (
              <CircularProgress size={14} thickness={5} />
            ) : (
              <AttachIcon sx={{ fontSize: "0.9rem !important" }} />
            )
          }
          sx={{ textTransform: "none" }}
        >
          {isAttaching
            ? "Attaching…"
            : `Attach ${pendingCount || ""} file${
                pendingCount !== 1 ? "s" : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachFilesDialog;
